// The clinician's home - "My day": recent sessions, live processing status,
// active subject files, quick actions. Role-aware: admins land on Subjects,
// auditors on the audit page (see routes/index.js Home wrapper).
import React, { useEffect, useState } from 'react';
import {
  Box, Button, Card, CardContent, Chip, Grid, LinearProgress, Stack, Typography,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import InsightsIcon from '@mui/icons-material/Insights';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import lpsService from '../services/lpsService';
import { nounsFor } from '../utils/verticalNouns';

const PROCESSING = ['queued', 'transcribing', 'diarizing', 'analyzing'];
const fmtDate = (iso) => (iso || '').slice(0, 16).replace('T', ' ');

export default function MyDayPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [nouns, setNouns] = useState(nounsFor('general'));
  const navigate = useNavigate();

  useEffect(() => {
    const load = () => {
      lpsService.listRecordings().then(setRows).catch(() => {});
      lpsService.listSubjects().then(setSubjects).catch(() => {});
    };
    load();
    const t = setInterval(load, 5000);
    lpsService.getVertical().then((v) => setNouns(nounsFor(v.vertical))).catch(() => {});
    return () => clearInterval(t);
  }, []);

  const inFlight = rows.filter((r) => PROCESSING.includes(r.status));
  const recent = rows.slice(0, 5);
  const firstName = (user?.first_name || user?.email || '').split(/[@\s]/)[0];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
        {firstName ? `Good to see you, ${firstName}` : 'My day'}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Everything here is processed inside your environment — nothing leaves it.
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { icon: <CloudUploadIcon />, label: `New ${nouns.session.toLowerCase()}`, to: '/recordings', variant: 'contained' },
          { icon: <FolderSharedIcon />, label: nouns.subjects, to: '/subjects', variant: 'outlined' },
          { icon: <QuestionAnswerIcon />, label: 'Ask my records', to: '/chat', variant: 'outlined' },
          { icon: <InsightsIcon />, label: 'My practice', to: '/practice', variant: 'outlined' },
        ].map((a) => (
          <Grid item key={a.to + a.label}>
            <Button variant={a.variant} startIcon={a.icon} onClick={() => navigate(a.to)}>
              {a.label}
            </Button>
          </Grid>
        ))}
      </Grid>

      {inFlight.length > 0 && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography fontWeight={700} sx={{ mb: 1 }}>Processing now</Typography>
            {inFlight.map((r) => (
              <Stack key={r.id} direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
                <Typography sx={{ flex: 1 }}>{r.title}</Typography>
                <Chip size="small" label={r.status} color="info" />
                <LinearProgress sx={{ width: 120 }} />
              </Stack>
            ))}
          </CardContent>
        </Card>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <Card variant="outlined">
            <CardContent>
              <Typography fontWeight={700} sx={{ mb: 1 }}>Recent {nouns.sessions.toLowerCase()}</Typography>
              {recent.map((r) => (
                <Stack key={r.id} direction="row" alignItems="center" spacing={1.5} sx={{ py: 0.75 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography noWrap fontWeight={600}>{r.title}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {fmtDate(r.created_at)}{r.subject_id ? ` · ${r.subject_id}` : ''}
                    </Typography>
                  </Box>
                  <Chip size="small" label={r.status}
                    color={r.status === 'done' ? 'success' : r.status === 'failed' ? 'error' : 'info'} />
                  <Button size="small" disabled={r.status !== 'done'}
                    onClick={() => navigate(`/recordings/${r.id}`)}>Open</Button>
                </Stack>
              ))}
              {!recent.length && (
                <Typography color="text.secondary" sx={{ py: 3 }} align="center">
                  Nothing yet — upload your first {nouns.session.toLowerCase()}.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={5}>
          <Card variant="outlined">
            <CardContent>
              <Typography fontWeight={700} sx={{ mb: 1 }}>Active {nouns.subjects.toLowerCase()}</Typography>
              {subjects.slice(0, 6).map((s) => (
                <Stack key={s.key || s.subject_id} direction="row" alignItems="center" spacing={1.5} sx={{ py: 0.75 }}>
                  <FolderSharedIcon color="primary" fontSize="small" />
                  <Typography sx={{ flex: 1 }} fontWeight={600}>{s.subject_id}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {s.sessions} {s.sessions === 1 ? nouns.session.toLowerCase() : nouns.sessions.toLowerCase()}
                  </Typography>
                  <Button size="small" disabled={!s.key}
                    onClick={() => s.key && navigate(`/subjects/${encodeURIComponent(s.key)}`)}>
                    File
                  </Button>
                </Stack>
              ))}
              {!subjects.length && (
                <Typography color="text.secondary" sx={{ py: 3 }} align="center">
                  {nouns.subject} files appear when {nouns.sessions.toLowerCase()} carry a {nouns.subject.toLowerCase()} ID.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
